import React, { useState, useEffect } from 'react';
import { X, Calendar, User, Clock, Euro, ChevronDown, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { participantService } from '../services/participantService';
import eventService from '../services/eventService';
import paymentService from '../services/paymentService';
import authService from '../services/authService';
import ParticipantSelectionModal from '../components/ParticipantSelectionModal';
import CreateParticipantInline from '../components/CreateParticipantInline';

const styles = `
.event-booking-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.create-participant-btn{
  margin-top: 1rem;
}

.event-booking-modal {
  background-color: white;
  border-radius: 0.5rem;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.event-booking-header {
  background: linear-gradient(135deg, #5A9C9C 0%, #4A8080 100%);
  color: white;
  padding: 2rem;
  position: relative;
}

.event-booking-close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1.5rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s;
}

.event-booking-close-btn:hover {
  opacity: 0.7;
}

.event-booking-title {
  font-size: 1.75rem;
  margin: 0 0 0.5rem 0;
  font-weight: 300;
}

.event-booking-subtitle {
  font-size: 1rem;
  margin: 0;
  opacity: 0.9;
}

.event-booking-body {
  padding: 2rem;
}

.booking-section {
  margin-bottom: 2rem;
}

.booking-section:last-child {
  margin-bottom: 0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
}

.section-title svg {
  color: #5A9C9C;
}

.section-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #f0f9f9;
  border-left: 3px solid #5A9C9C;
  border-radius: 0.25rem;
}

.select-participants-btn {
  width: 100%;
  padding: 1.25rem;
  border: 2px dashed #5A9C9C;
  border-radius: 0.5rem;
  background-color: #f0f9f9;
  color: #4A8080;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.select-participants-btn:hover {
  background-color: #e6f4f4;
  border-color: #4A8080;
}

.select-participants-btn.has-selection {
  border-style: solid;
  border-color: #4A8080;
  background-color: #e6f4f4;
}

.selected-participants-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
}

.selected-participant-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid #4A8080;
  border-radius: 0.5rem;
  background-color: #f0f9f9;
}

.participant-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.participant-badge.child {
  background-color: #dbeafe;
  color: #1e40af;
}

.participant-badge.adult {
  background-color: #f3e8ff;
  color: #6b21a8;
}

.participant-info-main {
  flex: 1;
}

.participant-name {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.25rem 0;
}

.participant-details {
  font-size: 0.875rem;
  color: #6b7280;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.participant-detail-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.allergy-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: #fef3c7;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #92400e;
  margin-top: 0.5rem;
}

.day-accordion {
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  overflow: hidden;
  transition: all 0.3s;
}

.day-accordion.has-selections {
  border-color: #5A9C9C;
  background-color: #f0f9f9;
}

.day-accordion-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 1px solid #e5e7eb;
  background: white;
}

.day-accordion.has-selections .day-accordion-header {
  background-color: #f0f9f9;
}

.day-accordion-header:hover {
  background-color: #f9fafb;
}

.day-mini-image {
  width: 250px;
  height: 250px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #e5e7eb;
}

.day-mini-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.day-info {
  flex: 1;
  min-width: 0;
}

.day-date-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.day-weekday {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
  text-transform: capitalize;
}

.day-full-date {
  font-size: 0.75rem;
  color: #6b7280;
}

.day-mini-description {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.125rem;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.day-selection-badge {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%);
  border-radius: 1rem;
  color: #0f766e;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.day-accordion-toggle {
  flex-shrink: 0;
  transition: transform 0.2s;
  color: #6b7280;
}

.day-accordion-toggle.expanded {
  transform: rotate(180deg);
}

@media (max-width: 768px) {
  .day-accordion-header {
    padding: 0.75rem;
    gap: 0.75rem;
  }

  .day-mini-image {
    width: 70px;
    height: 70px;
  }

  .day-weekday {
    font-size: 0.8rem;
  }

  .day-full-date {
    font-size: 0.7rem;
  }

  .day-mini-description {
    font-size: 0.7rem;
  }

  .day-selection-badge {
    padding: 0.25rem 0.5rem;
    font-size: 0.7rem;
  }
}

.day-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.day-date-info {
  flex: 1;
}

.day-weekday {
  font-size: 0.875rem;
  color: #6b7280;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.day-full-date {
  font-size: 1rem;
  color: #374151;
  font-weight: 500;
  margin-top: 0.125rem;
}

.day-selection-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  background-color: #5A9C9C;
  color: white;
  border-radius: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.day-selection-badge svg {
  width: 16px;
  height: 16px;
}

.day-accordion-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  color: #6b7280;
  transition: transform 0.3s;
}

.day-accordion-toggle.expanded {
  transform: rotate(180deg);
}

.day-accordion-content {
  padding: 0;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.day-accordion-content.expanded {
  max-height: 1000px;
  transition: max-height 0.5s ease-in;
}

.timeslots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  background-color: white;
}

.timeslot-card {
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}

.timeslot-card:hover:not(.disabled) {
  border-color: #5A9C9C;
  background-color: #f0f9f9;
}

.timeslot-card.selected {
  border-color: #4A8080;
  background-color: #e6f4f4;
}

.timeslot-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f9fafb;
}

.timeslot-full-badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: #ef4444;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.timeslot-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.timeslot-name {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.timeslot-checkbox {
  width: 20px;
  height: 20px;
  accent-color: #5A9C9C;
  cursor: pointer;
}

.timeslot-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.timeslot-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
}

.timeslot-price {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 1rem;
  font-weight: 700;
  color: #5A9C9C;
}

.timeslot-capacity {
  font-size: 0.875rem;
  color: #6b7280;
}

.timeslot-capacity.low {
  color: #dc2626;
  font-weight: 600;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
}

.day-card {
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  cursor: pointer;
  text-align: center;
  transition: all 0.3s;
}

.day-card:hover {
  border-color: #5A9C9C;
  background-color: #f0f9f9;
}

.day-card.selected {
  border-color: #4A8080;
  background-color: #e6f4f4;
}

.day-label {
  font-size: 0.875rem;
  color: #6b7280;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.day-date {
  font-size: 1rem;
  color: #374151;
  font-weight: 500;
}

.selection-summary {
  background-color: #f0f9f9;
  border: 2px solid #5A9C9C;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75rem 0;
  border-bottom: 1px solid #d1d5db;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-label {
  font-weight: 600;
  color: #374151;
}

.summary-value {
  font-weight: 700;
  color: #5A9C9C;
  font-size: 1.125rem;
}

.children-names-list {
  color: #6b7280;
  text-align: right;
  max-width: 60%;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.btn-secondary {
  flex: 1;
  padding: 0.875rem 1.5rem;
  background-color: white;
  color: #6b7280;
  border: 2px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  flex: 2;
  padding: 0.875rem 1.5rem;
  background: linear-gradient(135deg, #5A9C9C 0%, #4A8080 100%);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(90, 156, 156, 0.3);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.loading-state, .error-state {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.error-state {
  color: #dc2626;
}
`;

const EventBookingModal = ({ event, onClose, onSuccess }) => {
  const navigate = useNavigate();

  const [participants, setParticipants] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [showCreateParticipant, setShowCreateParticipant] = useState(false);
  const [eventImages, setEventImages] = useState({ images: [], dailyDetails: [] });
  const [eventTimeSlots, setEventTimeSlots] = useState([]);
  const [timeSlotAvailabilityByDay, setTimeSlotAvailabilityByDay] = useState({});


  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paymentType, setPaymentType] = useState('deposit');

  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedTimeSlotsByDay, setSelectedTimeSlotsByDay] = useState({});
  const [expandedDays, setExpandedDays] = useState({});

  const loadEventImages = async () => {
    try {
      const images = await eventService.getEventImages(event.id);
      setEventImages(images);
    } catch (err) {
      console.error('Erro ao carregar imagens:', err);
      setEventImages({ images: [], dailyDetails: [] });
    }
  };

  const loadEventTimeSlots = async () => {
    try {
      const timeSlots = await eventService.getEventTimeSlots(event.id);
      setEventTimeSlots(timeSlots);
    } catch (err) {
      console.error('Erro ao carregar timeslots:', err);
      setEventTimeSlots([]);
    }
  };

  const loadParticipants = async () => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      const data = await participantService.getParticipants();
      setParticipants(data);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar participantes. Tente novamente.');
      console.error('Error loading participants:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEventImages();
    loadEventTimeSlots();
    loadParticipants();

    if (hasTimeSlots()) {
      if (event.eventType === 'multi') {
        const firstDay = formatDateToString(new Date(event.startDate));
        setExpandedDays({ [firstDay]: true });
        loadTimeSlotAvailabilityForDay(firstDay);
      } else {
        const eventDate = formatDateToString(new Date(event.startDate));
        loadTimeSlotAvailabilityForDay(eventDate);
      }
    }
  }, [event]);

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  };

  const hasTimeSlots = () => {
    if (eventTimeSlots && eventTimeSlots.length > 0) return true;
    return event?.timeSlots && event.timeSlots.length > 0;
  };

  const handleParticipantCreated = async (newParticipant) => {
    try {
      const response = await participantService.getParticipants();
      setParticipants(response);

      setShowCreateParticipant(false);

    } catch (error) {
      console.error('Erro ao recarregar participantes:', error);
    }
  };

  const getAvailableDays = () => {
    if (!event) return [];

    const days = [];

    if (event.eventType === 'single') {
      const start = new Date(event.startDate);
      days.push(start);
    } else {
      if (eventImages.dailyDetails && eventImages.dailyDetails.length > 0) {
        eventImages.dailyDetails.forEach(detail => {
          const date = new Date(detail.date);
          days.push(date);
        });
      } else {
        const start = new Date(event.startDate);
        const end = new Date(event.endDate);
        let current = new Date(start);

        while (current <= end) {
          days.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
      }
    }

    return days;
  };

  const availableDays = getAvailableDays();

  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDayLabel = (date) => {
    const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    return days[date.getDay()];
  };

  const formatDayDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleParticipantsSelected = (selected) => {
    setSelectedParticipants(selected);
    setShowParticipantModal(false);
  };

  const toggleDayExpansion = async (dayString) => {
    setExpandedDays(prev => ({
      ...prev,
      [dayString]: !prev[dayString]
    }));

    if (!expandedDays[dayString] && hasTimeSlots()) {
      await loadTimeSlotAvailabilityForDay(dayString);
    }
  };

  const loadTimeSlotAvailabilityForDay = async (dayString) => {
    try {
      const availability = await eventService.getTimeSlotAvailability(event.id, dayString);
      setTimeSlotAvailabilityByDay(prev => ({
        ...prev,
        [dayString]: availability
      }));
    } catch (err) {
      console.error('Erro ao carregar disponibilidade:', err);
    }
  };

  const handleToggleTimeSlot = (dayString, slotId) => {
    setSelectedTimeSlotsByDay(prev => {
      const currentDaySlots = prev[dayString] || [];
      const isSelected = currentDaySlots.includes(slotId);

      if (isSelected) {
        const newDaySlots = currentDaySlots.filter(id => id !== slotId);
        if (newDaySlots.length === 0) {
          const { [dayString]: removed, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [dayString]: newDaySlots
        };
      } else {
        return {
          ...prev,
          [dayString]: [...currentDaySlots, slotId]
        };
      }
    });
  };

  const isTimeSlotSelectedForDay = (dayString, slotId) => {
    return (selectedTimeSlotsByDay[dayString] || []).includes(slotId);
  };

  const getDaySelectedSlotsCount = (dayString) => {
    return (selectedTimeSlotsByDay[dayString] || []).length;
  };

  const getSlotForDay = (dayString, slotId) => {
    if (timeSlotAvailabilityByDay[dayString]) {
      const daySlot = timeSlotAvailabilityByDay[dayString].find(s => s.id === slotId);
      if (daySlot) return daySlot;
    }
    return eventTimeSlots.find(s => s.id === slotId);
  };

  const isTimeSlotAvailable = (slot, dayString = null) => {
    if (dayString && event.eventType === 'multi' && timeSlotAvailabilityByDay[dayString]) {
      const daySlot = timeSlotAvailabilityByDay[dayString].find(s => s.id === slot.id);
      if (daySlot) {
        return daySlot.availableSpots > 0;
      }
    }

    const availableSpots = slot.availableSpots !== undefined
      ? slot.availableSpots
      : (slot.maxCapacity - (slot.currentBookings || 0));
    return availableSpots > 0;
  };

  const handleToggleDay = (dayString) => {
    setSelectedDays(prev =>
      prev.includes(dayString)
        ? prev.filter(d => d !== dayString)
        : [...prev, dayString]
    );
  };

  const calculateTotalPrice = () => {
    const numParticipants = selectedParticipants.length;

    if (hasTimeSlots()) {
      let total = 0;
      Object.entries(selectedTimeSlotsByDay).forEach(([day, slotIds]) => {
        slotIds.forEach(slotId => {
          const slot = eventTimeSlots.find(s => s.id === slotId);
          if (slot) {
            total += slot.price * numParticipants;
          }
        });
      });
      return total;
    } else if (event.eventType === 'multi') {
      return event.price * selectedDays.length * numParticipants;
    } else {
      return event.price * numParticipants;
    }
  };

  const getTotalSelectedSlots = () => {
    return Object.values(selectedTimeSlotsByDay).reduce((sum, slots) => sum + slots.length, 0);
  };

  const isConfirmDisabled = () => {
    if (selectedParticipants.length === 0) return true;

    if (hasTimeSlots()) {
      return getTotalSelectedSlots() === 0;
    } else if (event.eventType === 'multi') {
      return selectedDays.length === 0;
    }

    return false;
  };

  const handleConfirm = async () => {
    if (isConfirmDisabled() || submitting) return;

    setSubmitting(true);
    setError('');

    try {
      const reservationData = {
        numberOfParticipants: selectedParticipants.length,
        participantIds: selectedParticipants.map(p => p.id),
        selectedDays: [],
        selectedTimeSlots: []
      };

      if (hasTimeSlots()) {
        Object.entries(selectedTimeSlotsByDay).forEach(([day, slotIds]) => {
          slotIds.forEach(slotId => {
            const slot = eventTimeSlots.find(s => s.id === slotId);
            if (slot) {
              if (!slot.name || !slot.startTime || !slot.endTime) {
                console.error('Slot com dados incompletos:', slot);
                throw new Error(`Dados incompletos no horário: ${slot.name || slotId}`);
              }

              reservationData.selectedTimeSlots.push({
                TimeSlotId: slotId,
                TimeSlotName: slot.name,
                StartTime: slot.startTime,
                EndTime: slot.endTime,
                Day: day,
                Price: slot.price || 0
              });
            } else {
              console.error('Slot não encontrado:', slotId);
              throw new Error(`Horário não encontrado: ${slotId}`);
            }
          });
        });
      }
      else if (event.eventType === 'multi' && !hasTimeSlots()) {
        reservationData.selectedDays = selectedDays;
      }

      const response = await eventService.reserveEvent(event.id, reservationData);

      const bookingId = response.reservationId;

      if (!bookingId) {
        throw new Error('ID da reserva não foi retornado pelo servidor');
      }

      const totalAmount = calculateTotalPrice();
      const amountToPay = paymentType === 'full' ? totalAmount : totalAmount / 2;
      const currentUrl = window.location.origin;

      const paymentSession = await paymentService.createCheckoutSession(
        bookingId,
        amountToPay,
        `${currentUrl}/pagamento?session_id={CHECKOUT_SESSION_ID}`,
        `${currentUrl}/eventos`,
        paymentType
      );

      window.location.href = paymentSession.sessionUrl;

    } catch (err) {
      setError(err.message || 'Erro ao processar reserva. Tente novamente.');
      console.error('Error confirming booking:', err);
      setSubmitting(false);
    }
  };

  if (!event) return null;

  return (
    <>
      <style>{styles}</style>
      <div className="event-booking-overlay" onClick={onClose}>
        <div className="event-booking-modal" onClick={(e) => e.stopPropagation()}>
          <div className="event-booking-header">
            <button className="event-booking-close-btn" onClick={onClose}>
              <X size={24} />
            </button>
            <h2 className="event-booking-title">{event.title}</h2>
            <p className="event-booking-subtitle">Reservar participação no evento</p>
          </div>

          <div className="event-booking-body">
            {loading ? (
              <div className="loading-state">A carregar participantes...</div>
            ) : error ? (
              <div className="error-state">{error}</div>
            ) : (
              <>
                <div className="booking-section">
                  <h3 className="section-title">
                    <User size={20} />
                    Selecionar Participantes
                  </h3>
                  <p className="section-description">
                    Escolha quem vai participar neste evento. Pode selecionar crianças e/ou adultos.
                  </p>

                  <button
                    className={`select-participants-btn ${selectedParticipants.length > 0 ? 'has-selection' : ''}`}
                    onClick={() => setShowParticipantModal(true)}
                  >
                    <User size={20} />
                    {selectedParticipants.length > 0
                      ? `${selectedParticipants.length} participante${selectedParticipants.length !== 1 ? 's' : ''} selecionado${selectedParticipants.length !== 1 ? 's' : ''}`
                      : 'Selecionar Participantes'}
                  </button>

                  {!showCreateParticipant && (
                    <button
                      type="button"
                      onClick={() => setShowCreateParticipant(true)}
                      className="create-participant-btn"
                    >
                      <Plus size={20} />
                      Criar Novo Participante
                    </button>
                  )}

                  {showCreateParticipant && (
                    <CreateParticipantInline
                      onParticipantCreated={handleParticipantCreated}
                      onCancel={() => setShowCreateParticipant(false)}
                    />
                  )}



                  {selectedParticipants.length > 0 && (
                    <div className="selected-participants-list">
                      {selectedParticipants.map((participant) => (
                        <div key={participant.id} className="selected-participant-card">
                          <span className={`participant-badge ${participant.type === 0 || participant.type === 'Child' ? 'child' : 'adult'}`}>
                            {participant.type === 0 || participant.type === 'Child' ? 'Criança' : 'Adulto'}
                          </span>
                          <div className="participant-info-main">
                            <h4 className="participant-name">{participant.nome}</h4>
                            <div className="participant-details">
                              <span className="participant-detail-item">
                                {participant.idade || calculateAge(participant.dataNascimento)} anos
                              </span>
                              <span className="participant-detail-item">
                                CC: {participant.cartaoCidadao}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {hasTimeSlots() && event.eventType === 'multi' && (
                  <div className="booking-section">
                    <h3 className="section-title">
                      <Calendar size={20} />
                      Selecionar Horários por Dia
                    </h3>
                    <p className="section-description">
                      Escolha os horários desejados para cada dia do evento
                    </p>
                    {availableDays.map((day, dayIndex) => {
                      const dayString = formatDateToString(day);
                      const isExpanded = expandedDays[dayString];
                      const selectedCount = getDaySelectedSlotsCount(dayString);

                      return (
                        <div
                          key={dayIndex}
                          className={`day-accordion ${selectedCount > 0 ? 'has-selections' : ''}`}
                        >
                          <div
                            className="day-accordion-header"
                            onClick={() => toggleDayExpansion(dayString)}
                          >
                            <div className="day-mini-image">
                              <img
                                src={(() => {
                                  const dayDetails = eventImages.dailyDetails?.find(d => {
                                    const detailDate = new Date(d.date).toISOString().split('T')[0];
                                    return detailDate === dayString;
                                  });
                                  return dayDetails?.image || eventImages.images?.[0] || "";
                                })()}
                                alt={formatDayLabel(day)}
                              />
                            </div>

                            <div className="day-info">
                              <div className="day-date-info">
                                <div className="day-weekday">{formatDayLabel(day)}</div>
                                <div className="day-full-date">{formatDayDate(day)}</div>
                                {(() => {
                                  const dayDetails = eventImages.dailyDetails?.find(d => {
                                    const detailDate = new Date(d.date).toISOString().split('T')[0];
                                    return detailDate === dayString;
                                  });
                                  return dayDetails?.description?.trim() && (
                                    <div className="day-mini-description">{dayDetails.description}</div>
                                  );
                                })()}
                              </div>
                            </div>
                            {selectedCount > 0 && (
                              <div className="day-selection-badge">
                                <Clock size={16} />
                                <span>{selectedCount} {selectedCount === 1 ? 'horário' : 'horários'}</span>
                              </div>
                            )}
                            <div className={`day-accordion-toggle ${isExpanded ? 'expanded' : ''}`}>
                              <ChevronDown size={20} />
                            </div>
                          </div>
                          <div className={`day-accordion-content ${isExpanded ? 'expanded' : ''}`}>
                            <div className="timeslots-grid">
                              {eventTimeSlots.map((slot) => {
                                const daySlot = getSlotForDay(dayString, slot.id);
                                const isSelected = isTimeSlotSelectedForDay(dayString, slot.id);
                                const isAvailable = isTimeSlotAvailable(daySlot, dayString);
                                const availableSpots = daySlot?.availableSpots !== undefined
                                  ? daySlot.availableSpots
                                  : (daySlot?.maxCapacity || slot.maxCapacity);
                                const isLowCapacity = availableSpots <= 5 && availableSpots > 0;

                                return (
                                  <div
                                    key={slot.id}
                                    className={`timeslot-card ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                                    onClick={() => isAvailable && handleToggleTimeSlot(dayString, slot.id)}
                                  >
                                    {!isAvailable && (
                                      <span className="timeslot-full-badge">Esgotado</span>
                                    )}

                                    <div className="timeslot-header">
                                      <h4 className="timeslot-name">{daySlot?.name || slot.name}</h4>
                                      {isAvailable && (
                                        <input
                                          type="checkbox"
                                          className="timeslot-checkbox"
                                          checked={isSelected}
                                          onChange={() => { }}
                                        />
                                      )}
                                    </div>

                                    <div className="timeslot-time">
                                      <Clock size={14} />
                                      <span>{daySlot?.startTime || slot.startTime} - {daySlot?.endTime || slot.endTime}</span>
                                    </div>

                                    <div className="timeslot-details">
                                      <div className="timeslot-price">
                                        <Euro size={14} />
                                        <span>{(daySlot?.price || slot.price).toFixed(2)}</span>
                                      </div>
                                      <div className={`timeslot-capacity ${isLowCapacity ? 'low' : ''}`}>
                                        {isAvailable
                                          ? `${availableSpots} vagas`
                                          : 'Sem vagas'}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {hasTimeSlots() && event.eventType !== 'multi' && (
                  <div className="booking-section">
                    <h3 className="section-title">
                      <Clock size={20} />
                      Selecionar Horários
                    </h3>
                    <div className="timeslots-grid">
                      {eventTimeSlots.map((slot) => {
                        const dayString = formatDateToString(new Date(event.startDate));
                        const daySlot = getSlotForDay(dayString, slot.id);
                        const isSelected = isTimeSlotSelectedForDay(dayString, slot.id);
                        const isAvailable = isTimeSlotAvailable(daySlot, dayString);
                        const availableSpots = daySlot?.availableSpots !== undefined
                          ? daySlot.availableSpots
                          : (daySlot?.maxCapacity || slot.maxCapacity);
                        const isLowCapacity = availableSpots <= 5 && availableSpots > 0;

                        return (
                          <div
                            key={slot.id}
                            className={`timeslot-card ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                            onClick={() => isAvailable && handleToggleTimeSlot(dayString, slot.id)}
                          >
                            {!isAvailable && (
                              <span className="timeslot-full-badge">Esgotado</span>
                            )}

                            <div className="timeslot-header">
                              <h4 className="timeslot-name">{slot.name}</h4>
                              {isAvailable && (
                                <input
                                  type="checkbox"
                                  className="timeslot-checkbox"
                                  checked={isSelected}
                                  onChange={() => { }}
                                />
                              )}
                            </div>

                            <div className="timeslot-time">
                              <Clock size={14} />
                              <span>{slot.startTime} - {slot.endTime}</span>
                            </div>

                            <div className="timeslot-details">
                              <div className="timeslot-price">
                                <Euro size={14} />
                                <span>{slot.price.toFixed(2)}</span>
                              </div>
                              <div className={`timeslot-capacity ${isLowCapacity ? 'low' : ''}`}>
                                {isAvailable
                                  ? `${availableSpots} vagas`
                                  : 'Sem vagas'}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!hasTimeSlots() && event?.eventType === 'multi' && (
                  <div className="booking-section">
                    <h3 className="section-title">
                      <Calendar size={20} />
                      Selecionar Dias
                    </h3>
                    <div className="days-grid">
                      {availableDays.map((day, index) => {
                        const dayString = formatDateToString(day);
                        const isSelected = selectedDays.includes(dayString);

                        const dayDetails = eventImages.dailyDetails?.find(d => {
                          const detailDate = new Date(d.date).toISOString().split('T')[0];
                          return detailDate === dayString;
                        });

                        const imageUrl = dayDetails?.image || eventImages.images?.[0] || "";
                        const hasDescription = dayDetails?.description?.trim();

                        return (
                          <div
                            key={index}
                            className={`day-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleToggleDay(dayString)}
                          >
                            <div className="day-checkbox">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => { }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>

                            <div className="day-image">
                              <img src={imageUrl} alt={formatDayLabel(day)} />
                              {isSelected && (
                                <div className="selected-overlay">âœ“</div>
                              )}
                            </div>

                            <div className="day-info">
                              <div className="day-header">
                                <Calendar size={16} />
                                <div className="day-date">
                                  {formatDayLabel(day)}, {formatDayDate(day)}
                                </div>
                              </div>

                              {hasDescription && (
                                <p className="day-description">{dayDetails.description}</p>
                              )}

                              <div className="day-price">
                                <Euro size={16} />
                                <span>€{event.price.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {selectedParticipants.length > 0 && (
                  <>
                    <div style={{
                      backgroundColor: '#f0f9f9',
                      border: '2px solid #5A9C9C',
                      borderRadius: '0.5rem',
                      padding: '1.5rem',
                      marginBottom: '1.5rem'
                    }}>
                      <h3 style={{
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        Tipo de Pagamento
                      </h3>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '1rem',
                          border: paymentType === 'deposit' ? '2px solid #5A9C9C' : '2px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          backgroundColor: paymentType === 'deposit' ? '#e6f4f4' : 'white',
                          transition: 'all 0.3s'
                        }}>
                          <input
                            type="radio"
                            name="paymentType"
                            value="deposit"
                            checked={paymentType === 'deposit'}
                            onChange={(e) => setPaymentType(e.target.value)}
                            style={{
                              marginRight: '0.75rem',
                              width: '1.25rem',
                              height: '1.25rem',
                              accentColor: '#5A9C9C',
                              cursor: 'pointer'
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, color: '#374151', marginBottom: '0.25rem' }}>
                              Pagar Caução (50%)
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                              Pagar €{(calculateTotalPrice() / 2).toFixed(2)} agora e €{(calculateTotalPrice() / 2).toFixed(2)} no dia
                            </div>
                          </div>
                          <div style={{
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: '#5A9C9C'
                          }}>
                            €{(calculateTotalPrice() / 2).toFixed(2)}
                          </div>
                        </label>

                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '1rem',
                          border: paymentType === 'full' ? '2px solid #5A9C9C' : '2px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          backgroundColor: paymentType === 'full' ? '#e6f4f4' : 'white',
                          transition: 'all 0.3s'
                        }}>
                          <input
                            type="radio"
                            name="paymentType"
                            value="full"
                            checked={paymentType === 'full'}
                            onChange={(e) => setPaymentType(e.target.value)}
                            style={{
                              marginRight: '0.75rem',
                              width: '1.25rem',
                              height: '1.25rem',
                              accentColor: '#5A9C9C',
                              cursor: 'pointer'
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, color: '#374151', marginBottom: '0.25rem' }}>
                              Pagar Total
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                              Pagar o valor total agora (sem pagamento no dia)
                            </div>
                          </div>
                          <div style={{
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: '#5A9C9C'
                          }}>
                            €{calculateTotalPrice().toFixed(2)}
                          </div>
                        </label>
                      </div>

                      <div style={{
                        marginTop: '1rem',
                        padding: '0.75rem',
                        backgroundColor: '#fff7ed',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        color: '#92400e'
                      }}>
                        {paymentType === 'deposit'
                          ? 'Com a caução, pagas 50% agora e o restante no dia do evento.'
                          : 'Com pagamento total, não terás que pagar nada no dia do evento.'}
                      </div>
                    </div>

                    <div className="selection-summary">
                      <div className="summary-item">
                        <span className="summary-label">Participantes:</span>
                        <span className="children-names-list">
                          {selectedParticipants.map(p => p.nome).join(', ')}
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Nº de participantes:</span>
                        <span className="summary-value">{selectedParticipants.length}</span>
                      </div>
                      {hasTimeSlots() && getTotalSelectedSlots() > 0 && (
                        <div className="summary-item">
                          <span className="summary-label">Horários selecionados:</span>
                          <span className="summary-value">{getTotalSelectedSlots()}</span>
                        </div>
                      )}
                      {!hasTimeSlots() && event?.eventType === 'multi' && selectedDays.length > 0 && (
                        <div className="summary-item">
                          <span className="summary-label">Dias selecionados:</span>
                          <span className="summary-value">{selectedDays.length}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="modal-actions">
                  <button className="btn-secondary" onClick={onClose} disabled={submitting}>
                    Cancelar
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleConfirm}
                    disabled={isConfirmDisabled() || submitting}
                  >
                    {submitting ? 'A processar...' : 'Prosseguir para Pagamento'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showParticipantModal && (
        <ParticipantSelectionModal
          isOpen={showParticipantModal}
          onClose={() => setShowParticipantModal(false)}
          onConfirm={handleParticipantsSelected}
          maxParticipants={null}
          eventType={event.eventType}
          preSelectedParticipants={selectedParticipants}
        />
      )}
    </>
  );
};

export default EventBookingModal;